import React, { useState } from 'react';
import { Form, Button, Card, Container, Row, Col, Alert, Modal } from 'react-bootstrap';
import { Link, Navigate } from 'react-router-dom';
import ApiHelper from '../../api/apiCallsHelper';
import config from '../../common/config';
import { jwtDecode } from 'jwt-decode';

const Login = () => {
  // Check if already logged in
  const isLoggedIn = localStorage.getItem('jwt') || sessionStorage.getItem('jwt');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
    emailForReset: ''
  });

  const [state, setState] = useState({
    loginError: '',
    errors: {},
    isLoading: false,
    redirectToDashboard: false,
    showForgotPassword: false,
    resetSuccess: false
  });

  const handleChange = (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    setFormData(prev => ({ ...prev, [name]: value }));
    setState(prev => ({ ...prev, loginError: '' }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setState(prev => ({ ...prev, errors }));
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, loginError: '' }));

    try {
      const request = {
        email: formData.email,
        password: formData.password
      };

      const result = await ApiHelper.post(
        `${config.trueFingerPrintsAPI.baseURL}/users/login`,
        request
      );

      if (result.status === 200) {
        const token = result.data;
        const decoded = jwtDecode(token);
        if (formData.rememberMe) {
          localStorage.setItem('jwt', token);
          localStorage.setItem('userName', decoded.user.name);
          localStorage.setItem('userRole', decoded.user.role);
        } else {
          sessionStorage.setItem('jwt', token);
          sessionStorage.setItem('userName', decoded.user.name);
          sessionStorage.setItem('userRole', decoded.user.role);
        }

        window.dispatchEvent(new Event('authChange'));

        setTimeout(() => {
          setState(prev => ({ ...prev, redirectToDashboard: true }));
        }, 150);
      }
    } catch (error) {
      console.log('Login error:', error);
      let errorMessage = 'An error occurred. Please try again.';

      // API helper throws error.response directly, which has data.message
      if (error.data && error.data.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setState(prev => ({ ...prev, loginError: errorMessage, isLoading: false }));
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.emailForReset) {
      setState(prev => ({
        ...prev,
        errors: { resetEmail: 'Email is required' }
      }));
      return;
    }

    if (!validateEmail(formData.emailForReset)) {
      setState(prev => ({
        ...prev,
        errors: { resetEmail: 'Please enter a valid email address' }
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, errors: {} }));

    try {
      const result = await ApiHelper.post(
        `${config.trueFingerPrintsAPI.baseURL}/users/reset-pass`,
        { email: formData.emailForReset }
      );

      if (result.status === 200) {
        setState(prev => ({
          ...prev,
          resetSuccess: true,
          showForgotPassword: false,
          isLoading: false
        }));
        setFormData(prev => ({ ...prev, emailForReset: '' }));

        setTimeout(() => {
          setState(prev => ({ ...prev, resetSuccess: false }));
        }, 5000);
      }
    } catch (error) {
      const errorMsg = error.data?.message || error.message || 'Email not found';
      setState(prev => ({
        ...prev,
        errors: { resetEmail: errorMsg },
        isLoading: false
      }));
    }
  };

  const toggleForgotPassword = (show) => {
    setState(prev => ({
      ...prev,
      showForgotPassword: show,
      errors: {}
    }));
    if (!show) {
      setFormData(prev => ({ ...prev, emailForReset: '' }));
    }
  };

  // Redirect if already logged in or after successful login
  if (isLoggedIn || state.redirectToDashboard) {
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      {/* Forgot Password Modal */}
      <Modal show={state.showForgotPassword} onHide={() => toggleForgotPassword(false)} centered>
        <Modal.Body className="px-2 pt-5 pb-5 text-center">
          <h4 className="headline m-0 p-0 text-center text-dark">
            Do you want to reset your password?
          </h4>
          <p className="mb-4 mt-2">
            Enter your email address in the field below and click on the Reset button.
            We will then send you a new password on your email address.
          </p>

          {state.resetSuccess && (
            <Alert variant="success">
              Your password has been reset and sent to your email account.
            </Alert>
          )}

          <Form.Control
            className="text-center p-0"
            type="email"
            placeholder="Email address..."
            name="emailForReset"
            value={formData.emailForReset}
            onChange={handleChange}
          />
          {state.errors.resetEmail && (
            <p className="text-danger mt-2">{state.errors.resetEmail}</p>
          )}

          <Button
            variant="danger"
            onClick={handleForgotPassword}
            disabled={state.isLoading}
            className="mt-3"
          >
            {state.isLoading ? 'Sending...' : 'Reset'}
          </Button>
        </Modal.Body>
      </Modal>

      <div className="d-flex justify-content-center w-100 min-vh-100 align-items-center">
          <Card className="form-get-started p-0">
            <h2 className="headline mb-3 mt-5 p-0 text-center text-dark">Login</h2>
            <Card.Body className="p-5">
              <Form onSubmit={handleSubmit}>
                  <Form.Group controlId="formBasicEmail" className="mb-3">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Your E-mail Address"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {state.errors.email && (
                      <p className="text-danger">{state.errors.email}</p>
                    )}
                  </Form.Group>

                  <Form.Group controlId="formBasicPassword" className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                    />
                    {state.errors.password && (
                      <p className="text-danger">{state.errors.password}</p>
                    )}
                  </Form.Group>

                  <Form.Group controlId="formBasicCheckbox" className="mb-0">
                    <Form.Check
                      className="pl-0"
                      type="checkbox"
                      label="Remember me"
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                    />
                    {state.loginError && (
                      <Form.Label className="errors">{state.loginError}</Form.Label>
                    )}
                  </Form.Group>

                  <br />

                  <Button
                    variant="primary btn-block"
                    type="submit"
                    disabled={state.isLoading}
                  >
                    {state.isLoading ? 'Logging in...' : 'Login'}
                  </Button>

                  <Button
                    variant="link"
                    className="p-0 mt-4 mb-2"
                    onClick={() => toggleForgotPassword(true)}
                  >
                    Forgot Password?
                  </Button>
                  <hr />
                  <div className="text-center mt-0 pt-0">
                    <h6 className="text-muted">Don't have an account?</h6>
                    <Link to="/signup">
                      <Button
                        variant="outline-primary d-flex justify-content-center mt-3"
                        className="w-100"
                      >
                        Sign Up Here
                      </Button>
                    </Link>
                  </div>
                </Form>
            </Card.Body>
          </Card>
      </div>
    </div>
  );
};

export default Login;
